import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { setDefaultOptions } from 'date-fns'
import i18next from 'i18next'

import {
  CurrencyCode,
  CurrencyFormatKey,
  DateTranslationKey,
  FALLBACK_LANGUAGE,
  FormTranslationKey,
  isValidCurrencyFormatKey,
  isValidDateTranslationKey,
  isValidFormTranslationKey,
  isValidNumberFormatKey,
  isValidTranslationKey,
  NumberFormatKey,
} from '@forto-dev/platform-localisation'

import { useAuth } from '@/features/auth'
import { UpdateUserProps } from '@/features/settings/api/settings.service'
import { useUserUpdate } from '@/features/settings/api/useUserUpdate'
import { error } from '@/utils/logger'

import { LanguageCode, supportedLanguages } from './language'
import { userlane } from '../userlane/UserlaneService'

export const useLocalisation = () => {
  const { t, i18n } = useTranslation()
  const { user, customer } = useAuth()
  const userMutation = useUserUpdate()

  const translateFormValidation = useCallback(
    (key: FormTranslationKey): string => {
      return t(key, { ns: 'form' })
    },
    [t]
  )

  const translateDate = useCallback(
    (key: DateTranslationKey, date: Date) => {
      return t(key, {
        date,
        ns: 'dates',
      })
    },
    [t]
  )

  const translateNumber = useCallback(
    (format: NumberFormatKey, value: number) => {
      return t(format, {
        value,
        ns: 'numbers',
      })
    },
    [t]
  )

  const translateCurrency = useCallback(
    (format: CurrencyFormatKey, value: number, currency: CurrencyCode) => {
      return t(format, {
        amount: value,
        currency,
        ns: 'numbers',
      })
    },
    [t]
  )

  const setLibrariesLocale = (languageCode: LanguageCode): void => {
    const locale = supportedLanguages.find((lang) => lang.code === languageCode)?.locale

    if (locale) {
      setDefaultOptions({
        locale,
      })
    }
  }

  function updatePreferredLanguage(languageCode: LanguageCode): void {
    if (customer && user) {
      const data: UpdateUserProps = {
        customerId: customer._id,
        userId: user._id,
        data: { preferredLanguage: languageCode },
      }
      try {
        userMutation.mutateAsync(data)
      } catch {
        error('Error updating preferred language')
      }
    }
  }

  function changeLanguage(languageCode: LanguageCode): void {
    i18next.changeLanguage(languageCode)

    userlane.changeLanguage(languageCode)

    updatePreferredLanguage(languageCode)
  }

  return {
    translate: t,
    translateFormValidation,
    translateDate,
    translateNumber,
    translateCurrency,
    currentLanguage: i18n.language || FALLBACK_LANGUAGE,
    changeLanguage,
    setLibrariesLocale,
    updatePreferredLanguage,
    supportedLanguages,
    isValidTranslationKey,
    isValidFormTranslationKey,
    isValidDateTranslationKey,
    isValidNumberFormatKey,
    isValidCurrencyFormatKey,
  }
}
